import useTranslation from "@babackapp/translations/use-translation";
import { Badge } from "@babackapp/ui/components/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@babackapp/ui/components/hover-card";
import { Layers, PencilIcon } from "lucide-react";
import type { ShopOverrideRuleDto } from "@babackapp/private-api-client/types";

const BADGE_CLASSNAME =
  "cursor-pointer text-xs gap-1 font-normal py-0.5 bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100";

// Format rule conditions as a single readable string
export function conditionsSummary(
  conditions: ShopOverrideRuleDto["conditions"],
  t: (key: string) => string,
): string {
  const conds = conditions?.rules ?? [];
  if (conds.length === 0) return "";
  const sep =
    conditions?.operator === "OR" ? ` ${t("misc.or")} ` : ` ${t("misc.and")} `;
  const fmtDate = (v: unknown): string => {
    try {
      return new Date(v as string | number).toLocaleDateString(
        navigator.language,
        { day: "2-digit", month: "2-digit", year: "numeric" },
      );
    } catch {
      return String(v);
    }
  };

  return conds
    .map((c) => {
      const field = t(`overrideRules.fields.${c.id}.label`);
      const op = t(`overrideRules.operators.${c.operator}.label`);
      let val: string;
      if (c.type === "DATE") {
        val = Array.isArray(c.value)
          ? c.value.map(fmtDate).join(" — ")
          : fmtDate(c.value);
      } else if (Array.isArray(c.value)) {
        val = c.value.join(", ");
      } else if (typeof c.value === "boolean") {
        val = c.value ? "✓" : "✗";
      } else {
        val = String(c.value ?? "");
      }
      return `${field} ${op} ${val}`;
    })
    .join(sep);
}

type FormatFeeValue = (rule: ShopOverrideRuleDto) => string;

type OverrideRuleBadgeProps = {
  rule: ShopOverrideRuleDto;
  formatFeeValue: FormatFeeValue;
  onClick: () => void;
};

export function OverrideRuleBadge({
  rule,
  formatFeeValue,
  onClick,
}: OverrideRuleBadgeProps) {
  const fee = formatFeeValue(rule);
  return (
    <Badge variant="outline" className={BADGE_CLASSNAME} onClick={onClick}>
      {rule.name}
      {fee && <span className="font-medium"> → {fee}</span>}
      <PencilIcon className="h-2.5 w-2.5 opacity-50" />
    </Badge>
  );
}

type OverrideRuleBadgeListProps = {
  rules: ShopOverrideRuleDto[];
  formatFeeValue: FormatFeeValue;
  onEdit: (rule: ShopOverrideRuleDto) => void;
};

/**
 * A single rule shows as its own badge. Several rules collapse into one
 * "N règles" badge, so the row keeps a constant width whatever the rule names
 * are; the hover card then lists them in full with their conditions.
 */
export function OverrideRuleBadgeList({
  rules,
  formatFeeValue,
  onEdit,
}: OverrideRuleBadgeListProps) {
  const t = useTranslation();

  if (rules.length === 0) return null;

  if (rules.length === 1) {
    return (
      <OverrideRuleBadge
        rule={rules[0]}
        formatFeeValue={formatFeeValue}
        onClick={() => onEdit(rules[0])}
      />
    );
  }

  return (
    <HoverCard openDelay={50}>
      <HoverCardTrigger asChild>
        <Badge
          variant="outline"
          className="shrink-0 cursor-default gap-1 text-xs font-normal py-0.5 bg-violet-50 text-violet-700 border-violet-200"
        >
          <Layers className="h-3 w-3 opacity-70" />
          {/* The ICU plugin bypasses i18next's `_one`/`_other` suffix lookup, so
              we target the plural form directly — count is always >= 2 here. */}
          {t("settings.carriers.returnFeeOverride.rulesCount_other", {
            count: rules.length,
            defaultValue: `${rules.length} règles`,
          })}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent align="end" className="w-auto max-w-sm">
        <div className="flex flex-col gap-2.5">
          {rules.map((rule) => {
            const summary = conditionsSummary(
              rule.conditions,
              t as (k: string) => string,
            );
            return (
              <div key={rule.id} className="flex flex-col items-start gap-1">
                <OverrideRuleBadge
                  rule={rule}
                  formatFeeValue={formatFeeValue}
                  onClick={() => onEdit(rule)}
                />
                {summary && (
                  <span className="text-xs text-muted-foreground">
                    {summary}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </HoverCardContent>
    </HoverCard>
  );
}
